import { useCallback, type ReactNode } from 'react'
import { Pressable, View } from 'react-native'

import { Button } from '@/src/elements/button/default'
import { DateTimePicker } from '@/src/elements/input'
import { LocalityModalInput } from '@/src/elements/input/LocalityModalInput/LocalityModalInput'
import { Icon } from '@/src/elements/media/Icon'
import { Typography } from '@/src/elements/text/Typography'
import { useWizardStore } from '@/src/features/settings/stores/useWizardStore'
import { GENDER } from '@/src/features/user/models/user.enum'
import { TEST_ID } from '@/src/general/constants/testId'
import { ICON } from '@/src/general/enums/icons'
import { isDateValid } from '@/src/general/utils/isDateValid'
import { useTranslate } from '@/src/lang/useTranslate'

import type { TLocation } from '../../../api/types/editAccountSettings.types'
import { WizardContentLayout } from '../../layouts/WizardContentLayout'

export const AboutYouStep = (): ReactNode => {
  const { t } = useTranslate()
  const { gender, location, setGender, setLocation, setBirthdate, birthdate } = useWizardStore()

  const isWoman = gender === GENDER.F
  const isMan = gender === GENDER.M

  const handleUncaughtLocationSubmit = useCallback(
    async (newLocation: TLocation | undefined) => (newLocation ? setLocation(newLocation) : undefined),
    [setLocation],
  )
  return (
    <WizardContentLayout title={t('user.profile.questions.title.wizard')} testID={TEST_ID.ME.WIZARD.ABOUT_YOU}>
      <View className="flex flex-col gap-10">
        {/* GENDER */}
        <View className="gap-4">
          <Typography colors="primary" center size="lg" bold>
            {t('user.profile.questions.you-are')}
          </Typography>

          <View className="flex flex-row gap-3">
            <View className="flex-1">
              <Button
                fullWidth
                variant={isWoman ? 'solid' : 'faded'}
                colors="primary"
                onPress={() => setGender(GENDER.F)}
              >
                {t('words.a-woman')}
              </Button>
            </View>
            <View className="flex-1">
              <Button
                fullWidth
                variant={isMan ? 'solid' : 'faded'}
                colors="primary"
                onPress={() => setGender(GENDER.M)}
              >
                {t('words.a-man')}
              </Button>
            </View>
          </View>
        </View>

        {/* LOCATION */}
        <View className="gap-4">
          <Typography colors="primary" center size="lg" bold>
            {t('user.profile.questions.your-location')}
          </Typography>

          <LocalityModalInput
            inputTestId={TEST_ID.ME.WIZARD.LOCATION}
            title={t('user.home-location')}
            placeholder={t('user.settings.click-to-fill')}
            onUncaughtSubmit={handleUncaughtLocationSubmit}
            renderTrigger={({ onTrigger }) => (
              <Pressable
                onPress={onTrigger}
                className="mx-auto flex w-full max-w-[300px] flex-row justify-between rounded-2xl border border-primary-input-unfocus bg-default-bg-white px-4 py-3 text-danger-text-placeholder"
              >
                <Typography colors="primary" size="md">
                  {location?.city || t('user.profile.questions.your-location')}
                </Typography>

                <Icon name={ICON.MARKER_PIN} />
              </Pressable>
            )}
          />
        </View>

        {/* BIRTHDATE */}
        <View className="gap-4">
          <Typography colors="primary" center size="lg" bold>
            {t('user.profile.questions.your-birthdate')}
          </Typography>

          <DateTimePicker
            mode="date"
            color="primary"
            value={birthdate ? new Date(birthdate) : undefined}
            onChange={(date) => date && isDateValid(date) && setBirthdate(date)}
            className="mx-auto max-w-[300px]"
          />
        </View>
      </View>
    </WizardContentLayout>
  )
}
